import React from 'react';
import { fmt } from './utils/fmt';
import { useRules } from '../../hooks/useRules';
import { getVerdictFromRules } from '../../utils/mapListing';

function ScoreDonut({ score, subscores, size = 120 }) {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const total = subscores.deal.max + subscores.income.max + subscores.object.max + subscores.location.max;
  const segments = [subscores.deal, subscores.income, subscores.object, subscores.location];

  let offset = 0;
  const paths = segments.map((seg) => {
    const pct = seg.score / total;
    const len = circ * pct;
    const gap = circ - len;
    const path = { color: seg.color, dasharray: `${len} ${gap}`, offset: -offset };
    offset += len;
    return path;
  });

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
        {paths.map((p, i) => (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={p.color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={p.dasharray} strokeDashoffset={p.offset}
            style={{ transition: 'stroke-dasharray 0.8s ease, stroke-dashoffset 0.8s ease' }} />
        ))}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[36px] font-bold text-white leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {score ?? '—'}
        </span>
        <span className="text-[11px] text-white/50 mt-0.5">/ 100</span>
      </div>
    </div>
  );
}

const DEFAULT_SUBSCORES = {
  deal:     { score: 0, max: 40, color: '#1DB3B3', label: 'Deal' },
  income:   { score: 0, max: 30, color: '#0E7A6B', label: 'Ertrag' },
  object:   { score: 0, max: 20, color: '#3FC0C0', label: 'Objekt' },
  location: { score: 0, max: 10, color: '#0B5C52', label: 'Lage' },
};

export default function VerdictHero({ baseline, signals, market, subscores, scoreDetails, deal, raw, onOpenAi }) {
  const rules = useRules();
  const score = baseline?.score;
  const safeSubscores = subscores || DEFAULT_SUBSCORES;

  const verdict = getVerdictFromRules(rules, score);
  const verdictLabel = verdict ? verdict.label : (score != null ? 'Offen' : 'Offen');

  const address = deal?.address || raw?.Address || '';
  const listingUrl = raw?.ListingURL || '';
  const kaufpreis = baseline?.kaufpreis;
  const wohnflaeche = baseline?.wohnflaeche;
  const zimmer = raw?.Rooms;
  const baujahr = baseline?.baujahr;
  const heizung = raw?.HeatingType;
  const energie = raw?.EnergyCertificate;

  const propItems = [
    kaufpreis && `${fmt(kaufpreis)} €`,
    wohnflaeche && `${wohnflaeche} m²`,
    zimmer && `${zimmer} Zi.`,
    baujahr && `Bj. ${baujahr}`,
    heizung,
    energie && `Energie ${energie}`,
  ].filter(Boolean);

  return (
    <div className="rounded-2xl px-8 py-6" style={{ background: 'linear-gradient(135deg, #0C3B3B 0%, #0C6B6B 100%)' }}>
      {/* Row 1: Donut + Objektdaten + Toggle/Actions */}
      <div className="flex items-center gap-6">
        {/* Score Donut */}
        <div className="flex flex-col items-center shrink-0">
          <ScoreDonut score={score} subscores={safeSubscores} size={120} />
          <div className="mt-1.5 text-[13px] font-bold" style={{ color: 'rgba(255,255,255,0.80)' }}>{verdictLabel}</div>
        </div>

        {/* Objektdaten */}
        <div className="flex-1 min-w-0">
          {address && (
            <div className="text-[18px] font-semibold text-white mb-1 truncate">{address}</div>
          )}
          {propItems.length > 0 && (
            <div className="flex flex-wrap items-center text-[14px]" style={{ color: 'rgba(255,255,255,0.60)' }}>
              {propItems.map((item, i) => (
                <span key={i} className="flex items-center">
                  {i > 0 && <span className="mx-2" style={{ color: 'rgba(255,255,255,0.25)' }}>|</span>}
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right: Action Buttons */}
        <div className="flex flex-col items-end gap-3 shrink-0">
          <div className="flex items-center gap-2">
            {onOpenAi && (
              <button
                onClick={onOpenAi}
                className="immo-ghost-btn flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-semibold text-white transition-all"
                style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                Deal-Zusammenfassung
              </button>
            )}
            {listingUrl && (
              <button
                onClick={() => window.open(listingUrl, '_blank')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold text-white transition-all"
                style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M10 6.5v3a1 1 0 01-1 1H2.5a1 1 0 01-1-1V3a1 1 0 011-1h3M7.5 1.5h3v3M5.5 6.5l5-5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Zum Inserat
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
